<template>
  <service-wrapper
    :title="$t('DEALS.CLEAR_BANKRUPTCY.TITLE')"
    :subtitle="$t('DEALS.CLEAR_BANKRUPTCY.PERSONAL_SUBTITLE')"
    :is-success="status === COMMERCIAL_DATA_STATUS_SUCCESS"
    :is-error="serviceHasUnexpectedStatus(status)"
    img="/images/clear.svg"
    :date="date"
    :json-data="jsonData"
    :provider-name="PROVIDER_CLEAR_ID_CONFIRM_PERSON"
    :service-name="$t('DEALS.CLEAR_KYB.SUBTITLE')"
    :service-type="$t('DEALS.CLEAR_KYB.TITLE')"
  >
    <confirm-service-start
      :date="date"
      :confirm-handler="startSearch"
      :confirm-modal-button-disabled="isSearchLoading"
      :status="status"
      :service-name="$t('DEALS.CLEAR_KYB.SUBTITLE')"
      :service-type="$t('DEALS.CLEAR_KYB.TITLE')"
      :is-loading="isSearchLoading"
      :has-json-view="!isEmpty(jsonData)"
      :provider-name="PROVIDER_CLEAR_ID_CONFIRM_PERSON"
    >
      <template #download>
        <document-download
          v-if="!!clearData && !isSearchLoading"
          :file-types="
            servicePdfUrl ? PDF_JSON_RESPONSE_FILE_TYPES : [$t('COMMON.JSON')]
          "
          @download="downloadClearReportDocument"
        />
      </template>
    </confirm-service-start>

    <template #content>
      <div class="space-y-6">
        <div v-if="status === COMMERCIAL_DATA_STATUS_NOT_STARTED">
          <div class="w-full">
            {{ $t("DEALS.CLEAR_BANKRUPTCY.DESCRIPTION") }}
          </div>
        </div>

        <service-error
          v-if="serviceHasUnexpectedStatus(status)"
          :have-response="!isEmpty(bankruptcyData)"
          :service-error-message="status"
        />

        <business-not-found v-if="isNotFound" :title="status" />
        <div v-else-if="showStatusMessage(status)">
          {{ status || "-" }}
        </div>

        <div v-if="showData" class="flex w-full flex-col space-y-6">
          <lf-scrollable-menu
            :scroll-length="200"
            class="top-left-0 top-right-0 pt-0"
          >
            <checkmark-tab
              v-for="[entityKey, entity] in Object.entries(
                clearDataPersonId?.PersonEntities || []
              )"
              :key="entityKey"
              :active="selectedEntityKey === String(entityKey)"
              class="mx-3 cursor-pointer"
              @click="handleTabClick(String(entityKey))"
            >
              {{ getEntityPersonName(entity) || "-" }}
              <template #suffix>
                {{ Math.round(Number(entity?.TotalScore || 0)) + "%" }}
              </template>
            </checkmark-tab>
          </lf-scrollable-menu>

          <submitted-data>
            <div class="flex flex-col">
              <div class="flex items-center space-x-2 pb-6">
                <icon-base
                  :icon="IconCompany"
                  :width="48"
                  :height="48"
                  class="mr-4 place-self-center"
                />
                <lf-h3>
                  {{
                    `${matchSubmittedData?.FirstName} ${matchSubmittedData?.LastName}` ||
                    ""
                  }}
                </lf-h3>
              </div>
              <div class="flex justify-between border-t py-6">
                <div class="flex items-center space-x-2">
                  <icon-base :icon="IconAddress" />

                  <div class="text-sm font-normal text-gray-600">
                    {{
                      formatAddress({
                        address_line: matchSubmittedData?.Street,
                        city: matchSubmittedData?.City,
                        state: matchSubmittedData?.State,
                        zip: matchSubmittedData?.ZipCode,
                      }) || "-"
                    }}
                  </div>
                </div>
                <div class="flex items-center">
                  <icon-base
                    :icon="IconTelephone"
                    :icon-color="UTIL_COLORS.DEFAULT"
                  />

                  <div class="ml-2 pr-4 text-sm font-normal text-gray-600">
                    {{ matchSubmittedData?.PhoneNumber || "-" }}
                  </div>
                </div>
              </div>
              <div class="grid grid-cols-3 justify-between border-t pt-9">
                <div class="flex justify-between pr-5">
                  <span>{{ $t("COMMON.FIRST_NAME") }}</span>
                  <span class="text-headline">
                    {{ matchSubmittedData?.FirstName || "-" }}
                  </span>
                </div>
                <div class="flex justify-between border-l px-5">
                  <span>{{ $t("COMMON.TIN") }}</span>
                  <span class="text-headline">
                    {{ matchSubmittedData?.FeinNumber || "-" }}
                  </span>
                </div>
                <div class="flex justify-between border-l pl-5">
                  <span>{{ $t("COMMON.SSN") }}</span>
                  <span class="text-headline">
                    {{ matchSubmittedData?.SSN || "-" }}
                  </span>
                </div>
                <div class="flex justify-between pt-5 pr-5">
                  <span>{{ $t("COMMON.LAST_NAME") }}</span>
                  <span class="text-headline">
                    {{ matchSubmittedData?.LastName || "-" }}
                  </span>
                </div>
              </div>
            </div>
          </submitted-data>
          <toggle-section
            section="bankruptcy"
            chevron-position="right"
            :has-padding="false"
            :is-open="isOpen"
            @toggle="isOpen = !isOpen"
          >
            <template #titleSection>
              <lf-h4>{{ $t("DEALS.CLEAR_BANKRUPTCY.BANKRUPTCY") }}</lf-h4>
            </template>
            <template #content>
              <div
                v-if="
                  !isEmpty(
                    bankruptcyData[0]?.SectionDetails?.OtherFlags?.Bankruptcy
                  )
                "
                class="bg-white p-4"
              >
                <div v-if="Array.isArray(bankruptcyDetailInfo)">
                  <div class="flex items-center px-6 py-4">
                    <span class="w-1/2 text-xs text-label">
                      {{ $t("DEALS.CLEAR_KYB.RISK_FLAG_NAME") }}
                    </span>
                    <span class="w-1/4 text-xs text-label">
                      {{ $t("DEALS.CLEAR_KYB.RISK_FLAG_HIT_INDICATOR") }}
                    </span>
                    <span class="w-1/4 text-xs text-label">
                      {{ $t("DEALS.CLEAR_KYB.RISK_FLAG_SCORE") }}
                    </span>
                  </div>
                  <div v-if="!!bankruptcyDetailInfo?.length">
                    <div
                      v-for="(info, i) in bankruptcyDetailInfo"
                      :key="i"
                      class="mb-4 rounded-sm border py-4"
                    >
                      <div class="flex items-center border-b px-6 py-2">
                        <span class="w-1/2 text-sm text-headline">
                          {{ info?.riskInfo.name || "-" }}
                        </span>
                        <div class="w-1/4">
                          <div class="w-max text-sm text-headline">
                            <lf-pill :color-index="15">
                              {{ info?.riskInfo.indicator || "-" }}
                            </lf-pill>
                          </div>
                        </div>
                        <span class="w-1/4 text-sm text-label">
                          {{ info?.riskInfo.score || "0.00" }}
                        </span>
                      </div>
                      <div
                        v-if="
                          !!Object.keys(info?.details || {}).length ||
                          !!info?.identifierInfo.length
                        "
                        class="px-6"
                      >
                        <div
                          v-if="!!Object.keys(info?.details || {}).length"
                          :class="{ 'border-b': !!info?.identifierInfo.length }"
                        >
                          <div
                            v-for="[key, value] in Object.entries(info.details)"
                            :key="key"
                            class="grid grid-cols-2 items-center justify-between space-x-2"
                          >
                            <span class="text-sm text-label">{{ key }}</span>
                            <span class="text-sm text-headline">
                              {{ value }}
                            </span>
                          </div>
                        </div>
                        <div
                          v-if="!!info?.identifierInfo.length"
                          class="space-y-2 pt-4"
                        >
                          <div
                            v-for="item in info?.identifierInfo || []"
                            :key="item.key"
                            class="flex w-full items-center justify-between space-x-2 pr-14"
                          >
                            <span class="text-sm text-label">
                              {{ item.key }}
                            </span>
                            <span
                              class="bg-grey-hover text-xxs font-bold text-label"
                            >
                              {{ item.value }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-sm text-label">
                {{ $t("DEALS.CLEAR_BANKRUPTCY.NO_DATA") }}
              </div>
            </template>
          </toggle-section>
        </div>
      </div>
    </template>
  </service-wrapper>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import isEmpty from "lodash/isEmpty";

import {
  COMMERCIAL_DATA_STATUS_NOT_STARTED,
  COMMERCIAL_DATA_STATUS_SUCCESS,
  NO_BUSINESS_FOUND_MESSAGE,
  PDF_JSON_RESPONSE_FILE_TYPES,
  PDF_SERVICES_NAMES,
  PROVIDER_CLEAR_ID_CONFIRM_PERSON,
  PROVIDER_CLEAR_PERSON_SEARCH,
  UTIL_COLORS,
} from "@/helpers/constants";
import {
  downloadFile,
  downloadObjectAsJson,
  getServiceFileUrlByName,
} from "@/helpers/files";
import { getServiceFileName } from "@/helpers/formatting";
import { formatAddress } from "@/helpers/formatting";
import {
  serviceHasUnexpectedStatus,
  showStatusMessage,
} from "@/helpers/services";
import {
  getEntityPersonName,
  useDealFiles,
  useDeals,
  useDealsBase,
} from "@/hooks/deals";
import { useServices } from "@/hooks/services";
import applicationsApiService from "@/services/modules/applications";

import ToggleSection from "@/components/ToggleSection.vue";
import ServiceError from "@/components/deals/dataServices/ServiceError.vue";
import ServiceWrapper from "@/components/deals/dataServices/ServiceWrapper.vue";
import IconAddress from "@/components/icons/IconAddress.vue";
import IconCompany from "@/components/icons/IconCompany.vue";
import IconTelephone from "@/components/icons/IconTelephone.vue";
import CheckmarkTab from "@/components/ui/LfCheckmarkTab.vue";
import BusinessNotFound from "@/views/businessCreditDeals/components/BusinessNotFound.vue";
import ConfirmServiceStart from "@/views/businessCreditDeals/components/ConfirmServiceStart.vue";
import DocumentDownload from "@/views/deals/components/DocumentDownload.vue";
import SubmittedData from "@/views/deals/components/shared/SubmittedData.vue";

import type { IBusiness } from "@/models/applications";
import type {
  Criteria,
  EIDVPersonSearchResult,
} from "@/models/commercialData/clear/ClearConfirmPersonSearch";
import type {
  IDocumentIdentifierInfo,
  IRiskFlagDetails,
  SectionResult,
} from "@/models/commercialData/clear/ClearRiskInformBusinessReport";
import type {
  ClearRiskInformPersonSearchResult,
  Section,
} from "@/models/commercialData/clear/ClearRiskInformPersonSearch";

const { activeDeal } = useDealsBase();
const { cleanBusinessValues } = useDeals();
const { dispatch } = useStore();
const { files } = useDealFiles();
const { getCommercialDataForProviders } = useServices();

const { t } = useI18n();

const isSearchLoading = ref(false);
const isOpen = ref(true);
const servicePdfUrl = computed(() =>
  getServiceFileUrlByName(
    PDF_SERVICES_NAMES.CLEAR_ID_CONFIRM_PERSON,
    files.value
  )
);

const jsonData = computed(
  () =>
    activeDeal.value?.commercialData?.clear?.clear_risk_inform_person_search ||
    null
);

const searchData = computed<ClearRiskInformPersonSearchResult | null>(
  () =>
    activeDeal.value?.commercialData?.clear?.clear_risk_inform_person_search
      ?.RiskInformPersonSearchResult || null
);

const bankruptcyData = computed<Section[]>(
  () =>
    searchData.value?.PersonEntity?.Sections?.Section?.filter(
      (section) => section.SectionName === "Custom"
    ) || []
);

const clearDataPersonId = computed<EIDVPersonSearchResult | null>(
  () =>
    activeDeal.value?.commercialData?.clear?.clear_id_confirm_person
      ?.EIDVPersonSearchResults?.EIDVPersonSearchResult || null
);

const getDetails = (data: IRiskFlagDetails): Record<string, string> => {
  return Object.entries(data).reduce(
    (result, [key, value]) =>
      typeof value === "object"
        ? { ...result, ...getDetails(value) }
        : { ...result, [key]: value },
    {}
  );
};

const getIdentifierInfo = (
  info: IDocumentIdentifierInfo | IDocumentIdentifierInfo[] | undefined
) => {
  if (!info) {
    return [];
  }
  return Array.isArray(info)
    ? info.map((item) => ({
        key: item.SourceName,
        value: item.DocumentGuid,
      }))
    : [
        {
          key: info.SourceName,
          value: info.DocumentGuid,
        },
      ];
};

const bankruptcyDetailInfo = computed(() => {
  const Bankruptcy =
    bankruptcyData.value[0]?.SectionDetails?.OtherFlags?.Bankruptcy || [];
  if (Bankruptcy) {
    return Object.values(Bankruptcy).map((element) => ({
      details: getDetails(element?.RiskFlagDetails || {}),
      identifierInfo: getIdentifierInfo(element?.DocumentIdentifierInfo),
      riskInfo: {
        indicator: element?.RiskFlagHitIndicator || "",
        name: element?.RiskFlagName || "",
        score: element?.RiskFlagScore || "0.00",
      },
    }));
  }
  return [];
});

const matchSubmittedData = computed<Criteria | null>(
  () =>
    activeDeal.value?.commercialData?.request_data?.clear
      ?.clear_id_confirm_person?.EIDVPersonSearchRequest?.Criteria || null
);

const handleTabClick = async (index: string) => {
  selectedEntityKey.value = index;
  const entity =
    clearDataPersonId.value?.PersonEntities[selectedEntityKey.value];
  if (!entity) {
    return;
  }
  const payload: Partial<IBusiness> = {
    ...activeDeal.value.business,
    clear_business_id: entity.EntityIdentifier,
  };
  await applicationsApiService.updateBusinessInfo(
    cleanBusinessValues(payload),
    activeDeal.value.id
  );
};

const clearData = computed<
  ClearRiskInformPersonSearchResult | SectionResult[] | null
>(() => {
  const searchData =
    activeDeal.value?.commercialData?.clear?.clear_risk_inform_person_search
      ?.RiskInformPersonSearchResult || null;

  return searchData;
});

const selectedEntityKey = ref(
  Object.keys(clearDataPersonId.value?.PersonEntities || {})[0]
);

const showData = computed(() => !!Object.values(clearData.value || {}).length);

const status = computed(
  () =>
    activeDeal.value?.commercialData?.statuses?.clear?.[
      PROVIDER_CLEAR_PERSON_SEARCH
    ] || ""
);

const isNotFound = computed(() =>
  status.value.toLowerCase().includes(NO_BUSINESS_FOUND_MESSAGE)
);

const date = computed(
  () =>
    activeDeal.value?.commercialData?.dates?.[PROVIDER_CLEAR_PERSON_SEARCH] ||
    ""
);

onMounted(() => {
  getCommercialDataForProviders([
    PROVIDER_CLEAR_PERSON_SEARCH,
    PROVIDER_CLEAR_ID_CONFIRM_PERSON,
  ]);
});

const startSearch = async () => {
  isSearchLoading.value = true;
  try {
    await dispatch("applications/enrichApp", {
      provider: PROVIDER_CLEAR_PERSON_SEARCH,
    });
  } finally {
    isSearchLoading.value = true;
    try {
      await dispatch("applications/enrichApp", {
        provider: PROVIDER_CLEAR_ID_CONFIRM_PERSON,
      });
    } finally {
      isSearchLoading.value = false;
    }
  }
};

const downloadClearReportDocument = (type: string) => {
  const fileName = getServiceFileName(
    activeDeal.value.client?.name || "",
    activeDeal.value.business?.business_legal_name || "",
    t("DEALS.CLEAR_KYB.KYB"),
    t("DEALS.CLEAR_KYB.CLEAR")
  );

  if (type === t("COMMON.JSON")) {
    return downloadObjectAsJson(clearData.value, fileName);
  }

  return downloadFile(servicePdfUrl.value || "", {}, `${fileName}.pdf`, true);
};
</script>

<style>
@reference "@/assets/tailwind.css";

.item .p-accordion-content {
  background-color: #f1f4f7 !important;
}
</style>
